import {
  createDraftSafeSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import { pick, isEmpty, omitBy } from "lodash";

import {
  AdditionalInsuredInput,
  PolicyholderType,
  CreateOrUpdateBasicPolicyDetailsInput,
  ContinueQuotePolicyFragmentFragment,
} from "@covertree/new-business-graphql";
import {
  PersonalDetailsBasicForm,
  ContactDetailsForm,
  PersonalDetailsForm,
} from "@covertree/new-business-forms";
import { determineIfNoEmailAddress } from "@covertree/utils";

import { createOrUpdateBasicPolicyDetailsWithHolder } from "../policy";

export type ExtraQuestions = Pick<
  CreateOrUpdateBasicPolicyDetailsInput,
  "hasAutoPolicy" | "physicallyMailDocuments" | "hasAnotherCoverTreePolicy"
> & { noEmail: boolean };

export interface PersonalDetailsState {
  personalDetails: PersonalDetailsForm;
  additionalInsured?: AdditionalInsuredInput;
  extraQuestions: ExtraQuestions;
  leadSource?: string;
}

const initialState: PersonalDetailsState = {
  personalDetails: {
    type: PolicyholderType.Person,
    firstName: "",
    middleName: "",
    lastName: "",
    primaryContactNumber: "",
    emailAddress: "",
    insuredDateOfBirth: "",
    numberOfVehicles: null,
    occupationType: null,
  },
  additionalInsured: undefined,
  extraQuestions: {
    hasAnotherCoverTreePolicy: false,
    hasAutoPolicy: false,
    physicallyMailDocuments: false,
    noEmail: false,
  },
  leadSource: undefined,
};

export const personalDetailsSlice = createSlice({
  name: "personalDetails",
  initialState,
  selectors: {
    selectPersonalDetailsFormDefaultValues: (state) => state.personalDetails,
    selectPersonalDetailsBasicFormDefaultValues: (
      state
    ): PersonalDetailsBasicForm => ({
      firstName: state.personalDetails.firstName,
      middleName: state.personalDetails.middleName,
      lastName: state.personalDetails.lastName,
      insuredDateOfBirth: state.personalDetails.insuredDateOfBirth,
    }),
    selectContactDetailsFormDefaultValues: (state): ContactDetailsForm => ({
      primaryContactNumber: state.personalDetails.primaryContactNumber,
      emailAddress: state.personalDetails.emailAddress,
    }),
    selectAdditionalInsured: (state) => state.additionalInsured,
    selectExtraPolicyDetails: (state) => state.extraQuestions,
    selectLeadSource: (state) => state.leadSource,
  },
  extraReducers: (builder) => {
    builder.addCase(
      createOrUpdateBasicPolicyDetailsWithHolder.fulfilled,
      (state, { payload }) => {
        if ((payload.additionalInsured ?? []).length > 0) {
          // TODO: Fix once multi additional insured is fixed
          const [{ adFirstName, adLastName, relationshipIndividual }] =
            payload.additionalInsured!;
          state.additionalInsured = {
            firstName: adFirstName,
            lastName: adLastName,
            relationship: relationshipIndividual,
          };
        }
      }
    );
  },
  reducers: {
    /**
     * Sets policyholder data in state
     * @param state
     * @param payload
     */
    setPersonalDetails: (
      state,
      {
        payload,
      }: PayloadAction<{
        personalDetails: PersonalDetailsForm | PersonalDetailsBasicForm;
      }>
    ) => {
      state.personalDetails = {
        ...state.personalDetails,
        ...payload.personalDetails,
        ...(!("middleName" in payload.personalDetails)
          ? { middleName: undefined }
          : {}),
        ...(!("secondaryContactNumber" in payload.personalDetails)
          ? { secondaryContactNumber: undefined }
          : {}),
      };
    },
    /**
     * Sets just contact details within personal details
     * @param state
     * @param payload
     */
    setContactDetailsWithinPersonalDetails: (
      state,
      {
        payload,
      }: PayloadAction<{
        personalDetails: ContactDetailsForm;
      }>
    ) => {
      const { emailAddress, primaryContactNumber } = payload.personalDetails;

      state.personalDetails = {
        ...state.personalDetails,
        emailAddress,
        primaryContactNumber,
      };
    },
    /**
     * Sets additional insured person data in state
     * @param state
     * @param payload
     */
    setAdditionalInsured: (
      state,
      { payload }: PayloadAction<AdditionalInsuredInput | undefined>
    ) => {
      state.additionalInsured = payload;
    },
    /**
     * Sets extra questions data in state
     * @param state
     * @param payload
     */
    setExtraQuestion: (
      state,
      { payload }: PayloadAction<Partial<ExtraQuestions>>
    ) => {
      state.extraQuestions = {
        ...state.extraQuestions,
        ...payload,
      };
    },
    /**
     * Set Personal Details based on Policy details in CYQ
     */
    setPersonalDetailsBasedOnPolicy: (
      state,
      {
        payload,
      }: PayloadAction<
        Pick<
          ContinueQuotePolicyFragmentFragment,
          | "policyholder"
          | "insuredDateOfBirth"
          | "additionalInsured"
          | "hasAutoPolicy"
          | "hasAnotherCoverTreePolicy"
          | "physicallyMailDocuments"
        >
      >
    ) => {
      state.personalDetails = {
        ...omitBy(
          pick(payload.policyholder, [
            "hasActivePolicies",
            "describeOrgType",
            "firstName",
            "lastName",
            "policyholderLocator",
            "type",
            "secondaryContactNumber",
            "middleName",
            "orgName",
            "orgType",
            "primaryContactNumber",
            "repRelationToOrg",
            "occupationType",
          ]),
          isEmpty
        ),
        numberOfVehicles: payload.policyholder.numberOfVehicles,
        emailAddress: determineIfNoEmailAddress(
          payload.policyholder.emailAddress,
          payload.policyholder.primaryContactNumber
        )
          ? ""
          : payload.policyholder.emailAddress,
        locator: payload.policyholder.policyholderLocator,
        insuredDateOfBirth: payload.insuredDateOfBirth,
      };
      if ((payload.additionalInsured || [])?.length > 0)
        state.additionalInsured = {
          firstName: payload.additionalInsured![0].adFirstName,
          lastName: payload.additionalInsured![0].adLastName,
          relationship: payload.additionalInsured![0].relationshipIndividual,
        };

      state.extraQuestions = {
        hasAutoPolicy: payload.hasAutoPolicy,
        hasAnotherCoverTreePolicy: payload.hasAnotherCoverTreePolicy,
        noEmail: determineIfNoEmailAddress(
          payload.policyholder.emailAddress,
          payload.policyholder.primaryContactNumber
        ),
        physicallyMailDocuments: payload.physicallyMailDocuments,
      };
    },
    /**
     * Resets state the initial value
     */
    resetPersonalDetails() {
      return initialState;
    },
    /**
     * Sets the utm source value
     */
    setLeadSource: (state, { payload }: PayloadAction<string>) => {
      state.leadSource = payload;
    },
  },
});

export const {
  actions: {
    setPersonalDetails,
    setPersonalDetailsBasedOnPolicy,
    setContactDetailsWithinPersonalDetails,
    setAdditionalInsured,
    setExtraQuestion,
    resetPersonalDetails,
    setLeadSource,
  },
  selectors: {
    selectPersonalDetailsFormDefaultValues,
    selectPersonalDetailsBasicFormDefaultValues,
    selectContactDetailsFormDefaultValues,
    selectAdditionalInsured,
    selectExtraPolicyDetails,
    selectLeadSource,
  },
} = personalDetailsSlice;

/**
 * Draft Selector, which grabs state of slice needed to API call
 * Can be used within reducers of other slices
 */
export const getNonPolicyholderPersonalDetails = createDraftSafeSelector(
  [selectAdditionalInsured, selectExtraPolicyDetails],
  (additionalInsured, extraQuestions) => ({
    additionalInsured,
    extraQuestions,
  })
);

/**
 * Draft Selector, which grabs state of slice needed to API call
 * Can be used within reducers of other slices
 */
export const getPersonalDetails = createDraftSafeSelector(
  [selectPersonalDetailsFormDefaultValues],
  (personalDetails) => ({
    personalDetails,
  })
);

/**
 * Draft Selector, which grabs Lead Source state needed to API call
 * Can be used within reducers of other slices
 */
export const getLeadSource = createDraftSafeSelector(
  [selectLeadSource],
  (leadSource) => leadSource
);
